// The ride view (docs/plans/2026-10-17-journey.md, section 4): the journey's middle scene, a 1D strip. The route strip
// and clock on top, the scene in the middle (inside the metro coach, or the cab from the side), and an event card with
// two choices (keys 1 and 2, or a click) that pauses the ride. The clock runs at the ride's real minutes over its game
// seconds, so it always lands on the time model's arrival.
import RIDE from './ride.json';
import COACH from './ride-coach.json';
import STATIONS from './stations.json';
import { PeopleLayer, SCALE } from './people';
import type { DrawEnt } from './render';
import { JourneyData, JourneyEvent, JourneyChoice, Routes, Route, RideStop, rideStops, rideGameS, clock } from '../shared/journey';
import { Line, trainPos } from '../shared/train';
import { approachId } from './announce';
import { LED, LedPhase, ledPhase, ledLines, scrollX, screenOf, disruptFrom, rasterise, dots, grid } from './led';

const NIGHT = '#121620', DUSK = '#2A2438', PAPER = '#F1ECE0', DIM = '#B9B2A4', SODIUM = '#FFB547', METRO = '#2F7DE1';
const APPROACH_M = 400, STATION_DWELL_S = 2.5, END_S = 1.4, JAM_S = 2.6;
const DOOR_S = 0.45; // seconds a door takes to slide its full width

/** A rect as fractions of the coach plate (src/client/ride-coach.json, made by tools/make-ride-coach.py). */
type Box = { x: number; y: number; w: number; h: number };
/** Where people stand in the coach, in metres either side of the doorway's middle: your group nearest the door. */
const SLOT_M = [1.3, 2.05, 2.8, 3.55, 4.3, 5.05];

/** What the ride needs from the journey: times in minutes of the day for the choices so far, and where to report. */
export interface RideHost {
  /** boarding, the end of this ride, and the arrival (with everything after the ride) */
  times(): { start: number; end: number; arrive: number; late: number };
  pick(event: string, choice: string): void;
  /** a choice swapped the ride (the cab's straight to the zone) */
  swap(ride: string): void;
  /** the ride ends here, in metres along it, after this choice (the rumour's get-off) */
  done(): void;
  companions(): number;
  chime(): void;
  /** a recorded announcement (src/client/announce.ts): one voice at a time, a busy line is dropped, prio 2 cuts in */
  say?(id: string, prio?: number): void;
}

/**
 * The shared metro (docs/plans/2026-10-18-shared-metro.md): online, the ride follows the server's train instead of its
 * own clock. Everyone aboard rides the same coach, and an event's card is a vote that runs while the train moves.
 */
export interface SharedRide {
  line: Line;
  /** seconds since this train left its first stop, eased between the server's updates */
  s(): number;
  /** who is aboard: [rider no, name], you included */
  aboard(): [number, string][];
  /** your rider no */
  me(): number;
  /** the open vote, if any: seconds left, the count so far and your own vote */
  vote(): { ev: string; left: number; n: Record<string, number>; mine: string | null } | null;
  /** the votes already decided on this train: event id -> choice id */
  results(): Record<string, string>;
  /** the HUD clock, in minutes of the day */
  clockMin(): number;
}

/** Words broken into lines no wider than `w` at the context's current font. */
function wrap(g: CanvasRenderingContext2D, text: string, w: number): string[] {
  const out: string[] = []; let line = '';
  for (const word of text.split(' ')) { const t = line ? `${line} ${word}` : word; if (line && g.measureText(t).width > w) { out.push(line); line = word; } else line = t; }
  if (line) out.push(line);
  return out;
}

type Sprite = { lengthM: number; w: number; h: number; fps: number; frames: number[][] };
const SPR = RIDE.vehicles as unknown as Record<string, Sprite>;
interface Passer { kind: string; x: number; lane: 0 | 1; v: number }

export class RideView {
  private route: Route;
  private stops: RideStop[];
  private d = 0;
  private endM: number;
  private v: number; // metres a game second while moving
  private t = 0;
  private card: RideStop | null = null;
  private fired = new Set<string>();
  private dwell = 0;
  private stationAt = -1; // index of the station being dwelt at
  private subtitle = { text: '', t: 0 };
  private jam = 0; // seconds the jam has held the ride
  private ending = 0;
  private delayShown = 0;
  private hits: { x: number; y: number; w: number; h: number; id: string }[] = [];
  private img = new Image();
  private coach = new Image(); private leaves = new Image();
  private art = new Map<string, HTMLImageElement>(); // station name -> its artwork, fetched on approach
  private passers: Passer[] = [];
  private boarded = 0; // students who boarded with you
  private standing = 0; // students who boarded and kept apart
  private sky: { x: number; w: number; h: number; lit: number[] }[][] = [];
  private finished = false;

  /** the passengers, drawn at the coach's scale (made again when the plate's drawn height changes) */
  private people: PeopleLayer | null = null; private peopleH = 0;

  /** online: the doors' opening from the server's train, 0 to 1 */
  private doorsShared = 0;

  /** the LED strip and the screen (docs/plans/2026-10-18-ride-led-and-screen.md): its text's key, seconds since it changed */
  private ledKey = ''; private ledT = 0;
  private ledImg: { key: string; font: boolean; hi: HTMLCanvasElement; en: HTMLCanvasElement } | null = null;
  private ledGrid: { cols: number; c: HTMLCanvasElement } | null = null;
  private screenA = 1; // the screen's brightness, lit on approach and dimmed once the doors are open
  private disrupt: string | null;

  constructor(private J: JourneyData, private R: Routes, private ride: string, private host: RideHost, private picks: () => Record<string, string>, private shared: SharedRide | null = null) {
    this.route = R[ride];
    this.stops = rideStops(J, R, ride);
    this.endM = this.route.lengthM;
    const stations = this.route.stations?.length ?? 0;
    const move = rideGameS(J, this.stops.length) - stations * STATION_DWELL_S;
    this.v = this.route.lengthM / Math.max(10, move);
    this.img.src = RIDE.image;
    this.coach.src = COACH.image; this.leaves.src = COACH.door_image;
    this.disrupt = disruptFrom(typeof location === 'undefined' ? '' : location.search, this.route.disrupt);
    // the skyline: two layers of blocks with lit windows, from a fixed seed so it is the same every ride
    let seed = 20261017;
    const rnd = () => ((seed = (seed * 1664525 + 1013904223) >>> 0) / 4294967296);
    for (const layer of [0, 1]) {
      const row = [];
      for (let x = 0; x < 2400; ) { const w = 60 + rnd() * 120; row.push({ x, w, h: (layer ? 120 : 200) + rnd() * (layer ? 140 : 200), lit: Array.from({ length: 24 }, () => rnd()) }); x += w + rnd() * 18; }
      this.sky.push(row);
    }
  }

  get mode() { return this.route.mode; }

  // ------------------------------------------------------------------ input
  key(code: string): boolean {
    if (!this.card || this.shared?.vote()?.mine) return false;
    const i = code === 'Digit1' || code === 'Numpad1' ? 0 : code === 'Digit2' || code === 'Numpad2' ? 1 : -1;
    if (i < 0 || !this.card.event.choices[i]) return false;
    this.choose(this.card.event.choices[i]);
    return true;
  }
  click(x: number, y: number): boolean {
    const h = this.hits.find((r) => x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h);
    if (!h || !this.card || this.shared?.vote()?.mine) return false;
    this.choose(this.card.event.choices.find((c) => c.id === h.id)!);
    return true;
  }

  private choose(c: JourneyChoice) {
    const e = this.card!.event;
    this.host.pick(e.id, c.id);
    if (this.shared) return; // a vote: the card stays up, with the count, until the server closes it
    this.card = null; this.hits = [];
    if (c.effect.companions) this.boarded += c.effect.companions;
    else if (e.id === 'students-board') this.standing = 3;
    if (c.effect.ride && this.R[c.effect.ride]) { // a new route from here: carry on at the same share of the way
      const share = this.d / this.route.lengthM;
      this.ride = c.effect.ride; this.route = this.R[c.effect.ride]; this.endM = this.route.lengthM; this.d = share * this.endM;
      this.host.swap(c.effect.ride);
    }
    if (c.effect.arrive && e.at.between) { // off at the next station, and the ride ends there
      const s = this.route.stations!.find((x) => x.name === e.at.between![1]);
      if (s) this.endM = s.atM;
    }
    if (typeof c.effect.lateMin === 'number' && e.id === 'jam') this.jam = JAM_S;
  }

  // ------------------------------------------------------------------ update
  update(dt: number) {
    this.t += dt;
    if (this.subtitle.t > 0) this.subtitle.t -= dt;
    if (this.route.mode === 'metro') this.stepLed(dt);
    if (this.shared) { this.follow(); return; }
    const T = this.host.times(), delay = T.end - T.start - this.baseMin();
    this.delayShown += (Math.max(0, delay) - this.delayShown) * Math.min(1, dt * (this.jam > 0 ? 1.4 : 3));
    if (this.finished) return;
    if (this.ending > 0) { this.ending -= dt; if (this.ending <= 0) { this.finished = true; this.host.done(); } return; }
    if (this.card) return;
    if (this.jam > 0) { this.jam -= dt; this.stepPassers(dt, 0); return; }
    if (this.dwell > 0) { this.dwell -= dt; this.stepPassers(dt, 0); return; }
    const before = this.d;
    this.d = Math.min(this.endM, this.d + this.v * dt);
    this.stepPassers(dt, this.v);
    this.announce();
    // stations: dwell, chime, say the name
    this.route.stations?.forEach((s, i) => {
      if (i > 0 && before < s.atM && this.d >= s.atM) {
        this.d = s.atM; this.dwell = STATION_DWELL_S; this.stationAt = i; this.host.chime();
        this.subtitle = { text: `${s.name} · ${s.hi}`, t: 1.6 };
      }
    });
    for (const st of this.stops) if (!this.fired.has(st.event.id) && this.d >= st.atM - 1e-6) {
      this.fired.add(st.event.id); this.card = st; this.d = Math.max(this.d, st.atM); return;
    }
    if (this.d >= this.endM - 1e-6) { this.ending = END_S; if (this.route.mode === 'metro') this.subtitle = { text: 'Doors open on the left · दरवाज़े बाईं ओर खुलेंगे', t: END_S }; if (this.route.mode === 'metro') this.host.say?.('pa-dock-left', 2); }
  }

  /**
   * The line for the next station, once the train is inside its braking zone. The ride is compressed and a line runs
   * ~11 s, so most are dropped by the one-voice rule; the last station is prio 2 and always heard.
   */
  private announce() {
    const st = this.route.stations; if (!st || this.route.mode !== 'metro') return;
    const i = st.findIndex((s, k) => k > 0 && s.atM > this.d);
    if (i > 0 && st[i].atM - this.d <= APPROACH_M) this.host.say?.(approachId(st[i].name), i === st.length - 1 ? 2 : 1);
  }

  /** Online: put the coach where the server's train is, open its doors at a stop, and show the open vote. */
  private follow() {
    const sh = this.shared!, L = sh.line, s = sh.s(), p = trainPos(L, s);
    this.d = p.atM;
    this.announce();
    // a vote that got the coach off early ends the ride at that station
    const res = sh.results();
    for (const e of this.J.events) {
      const ch = e.choices.find((c) => c.id === res[e.id]);
      const off = ch?.effect.arrive ? (e.at.between ? e.at.between[1] : e.at.station) : undefined;
      const st = off ? L.stops.find((x) => x.name === off) : undefined;
      if (st) this.endM = st.atM;
      if (e.id === 'students-board' && res[e.id] && !this.fired.has(e.id)) { this.fired.add(e.id); if (ch?.effect.companions) this.boarded = ch.effect.companions; else this.standing = 3; }
    }
    if (p.stop >= 0) {
      const at = s - L.stops[p.stop].arrS;
      this.doorsShared = Math.max(0, Math.min(1, at / DOOR_S, p.done ? 1 : (L.dwellS - at) / DOOR_S));
      if (this.stationAt !== p.stop) {
        this.stationAt = p.stop; this.host.chime();
        this.subtitle = { text: `${L.stops[p.stop].name} · ${L.stops[p.stop].hi}`, t: 1.6 };
      }
    } else this.doorsShared = 0;
    const v = sh.vote();
    this.card = v ? this.stops.find((x) => x.event.id === v.ev) ?? null : null;
    if (!this.card) this.hits = [];
  }

  /** Where the train is, for the strip and the screen: from the server's train online, else from this ride's own state. */
  private ledPhase(): LedPhase {
    if (this.shared) {
      const L = this.shared.line, p = trainPos(L, this.shared.s());
      return ledPhase(L.stops, p.atM, p.stop, p.done || (p.stop >= 0 && L.stops[p.stop].atM >= this.endM - 1), p.next);
    }
    return ledPhase(this.route.stations || [], this.d, this.dwell > 0 ? this.stationAt : -1, this.ending > 0 || this.finished);
  }

  /**
   * The strip's clock restarts when its text changes. A solo card pauses the ride and the strip holds where it is; a
   * shared card is a vote while the train moves, so it scrolls on. The screen lights inside the braking zone and dims
   * once the doors stand open.
   */
  private stepLed(dt: number) {
    const ph = this.ledPhase(), k = ledLines(ph, this.disrupt).key;
    if (k !== this.ledKey) { this.ledKey = k; this.ledT = 0; }
    else if (this.shared || (!this.card && this.jam <= 0)) this.ledT += dt;
    const lit = ph.kind === 'moving' ? ph.next.atM - this.d <= APPROACH_M || !!this.disrupt : this.doorOpen() < 1;
    this.screenA += ((lit ? 1 : 0.35) - this.screenA) * Math.min(1, dt * 4);
  }

  /** The ride's real minutes without the events' delays, to the end it now has. */
  private baseMin() {
    const T = this.host.times();
    const delays = Object.entries(this.picks()).reduce((a, [ev, ch]) => {
      const l = this.J.events.find((e) => e.id === ev)?.choices.find((c) => c.id === ch)?.effect.lateMin;
      return a + (typeof l === 'number' ? l : 0);
    }, 0);
    return T.end - T.start - delays;
  }

  /** The clock now: boarding time, plus the share of the way times the ride's minutes, plus the delays so far. */
  clockMin(): number {
    if (this.shared) return this.shared.clockMin();
    const T = this.host.times();
    return T.start + (this.d / this.endM) * this.baseMin() + this.delayShown;
  }

  private stepPassers(dt: number, v: number) {
    if (this.route.mode !== 'cab') return;
    const jam = this.jam > 0 || this.card?.event.id === 'jam';
    for (const p of this.passers) p.x += ((p.lane ? -p.v : p.v) - (jam ? 0 : v * 0.35)) * dt * (jam && !p.lane ? 0 : 1);
    this.passers = this.passers.filter((p) => p.x > -40 && p.x < 80);
    if (this.passers.length < 7 && Math.random() < dt * 1.2) {
      const kinds = ['sedan', 'sedan', 'box_truck', 'bus', 'sedan', 'container_truck'];
      const kind = kinds[Math.floor(Math.random() * kinds.length)], lane = Math.random() < 0.5 ? 0 : 1;
      const v = lane ? 8 + Math.random() * 6 : -2 + Math.random() * 5; // m/s against the cab's own pace
      this.passers.push({ kind, lane, v, x: lane ? 75 : v > 0 ? -35 : 75 });
    }
  }

  // ------------------------------------------------------------------ draw
  draw(g: CanvasRenderingContext2D, W: number, H: number) {
    g.fillStyle = NIGHT; g.fillRect(0, 0, W, H);
    const top = Math.min(110, H * 0.16), bottom = H;
    if (this.route.mode === 'metro') this.drawCoach(g, W, top, bottom - top);
    else this.drawStreet(g, W, top, bottom - top);
    this.drawStrip(g, W, top);
    if (this.subtitle.t > 0) {
      g.globalAlpha = Math.min(1, this.subtitle.t * 2);
      g.font = '600 18px Inter,system-ui,sans-serif'; g.textAlign = 'center'; g.lineWidth = 4; g.strokeStyle = 'rgba(0,0,0,.8)'; g.fillStyle = PAPER;
      g.strokeText(this.subtitle.text, W / 2, H - 28); g.fillText(this.subtitle.text, W / 2, H - 28); g.globalAlpha = 1;
    }
    if (this.card) this.drawCard(g, W, top + (H - top) * 0.42);
    if (this.ending > 0 || this.finished) { g.fillStyle = `rgba(18,22,32,${Math.min(1, 1 - this.ending / END_S)})`; g.fillRect(0, 0, W, H); }
  }

  /** The route strip: stations or named roads, the progress dot, the clock and the due time. */
  private drawStrip(g: CanvasRenderingContext2D, W: number, h: number) {
    g.fillStyle = '#0B0E15'; g.fillRect(0, 0, W, h);
    const x0 = 40, x1 = W - 280, y = h * 0.45, X = (m: number) => x0 + (m / this.route.lengthM) * (x1 - x0);
    g.lineCap = 'round'; g.lineWidth = 5;
    g.strokeStyle = '#4A4F5E'; g.beginPath(); g.moveTo(x0, y); g.lineTo(x1, y); g.stroke();
    g.strokeStyle = this.route.mode === 'metro' ? '#F2C230' : SODIUM; g.beginPath(); g.moveTo(x0, y); g.lineTo(X(this.d), y); g.stroke();
    g.font = '500 12px Inter,system-ui,sans-serif'; g.textAlign = 'center';
    const marks = this.route.mode === 'metro'
      ? (this.route.stations || []).map((s, i) => ({ m: s.atM, label: s.name, i }))
      : (this.route.roads || []).filter((r) => r[1] - r[0] > 500).map((r, i) => ({ m: (r[0] + r[1]) / 2, label: r[2], i }));
    let lastX = -1e9, alt = 0;
    for (const k of marks) {
      const x = X(k.m), past = k.m <= this.d;
      g.fillStyle = past ? '#F2C230' : PAPER; g.beginPath(); g.arc(x, y, 4, 0, Math.PI * 2); g.fill();
      alt = x - lastX < 80 ? 1 - alt : 0; lastX = x; // crowded labels alternate below and above
      g.fillStyle = past ? DIM : PAPER;
      g.fillText(k.label, x, alt ? y - 12 : y + 20);
    }
    if (this.endM < this.route.lengthM) { g.strokeStyle = SODIUM; g.lineWidth = 2; g.beginPath(); g.moveTo(X(this.endM), y - 10); g.lineTo(X(this.endM), y + 10); g.stroke(); }
    g.fillStyle = SODIUM; g.beginPath(); g.arc(X(this.d), y, 8, 0, Math.PI * 2); g.fill(); g.strokeStyle = NIGHT; g.lineWidth = 2; g.stroke();
    const T = this.host.times();
    g.textAlign = 'left'; g.fillStyle = PAPER; g.font = '400 38px "Bebas Neue",Impact,sans-serif';
    g.fillText(clock(this.clockMin()), W - 200, y + 6);
    g.font = '500 12px Inter,system-ui,sans-serif'; g.fillStyle = SODIUM;
    const late = Math.round(T.late), when = late === 0 ? 'on time' : late < 0 ? `${-late} min early` : `${late} min late`;
    g.fillText(`due ${clock(T.arrive)}: ${when} for ${this.J.clock.meetAt}`, W - 200, y + 26);
  }

  private nextStation() {
    const s = this.route.stations || [];
    return s.find((x) => x.atM > this.d + 1) || s[s.length - 1];
  }

  /**
   * Inside the coach (docs/plans/2026-10-18-ride-coach.md, section 4): what is behind the glass, the door leaves under
   * the plate so its frame and stanchions hide them, the plate, the route strip in its lit panel, then the passengers.
   * Every rect comes from ride-coach.json; the plate is drawn to cover and cropped, never stretched.
   */
  private drawCoach(g: CanvasRenderingContext2D, W: number, y0: number, h: number) {
    const ph = Math.max(h, W / COACH.aspect), pw = ph * COACH.aspect, px = (W - pw) / 2;
    const py = y0 - Math.min((ph - h) / 2, ph * 0.08); // a wide window crops the floor before the strip's panel
    const at = (b: Box): Box => ({ x: px + b.x * pw, y: py + b.y * ph, w: b.w * pw, h: b.h * ph });
    g.save(); g.beginPath(); g.rect(0, y0, W, h); g.clip();
    g.fillStyle = '#1C2130'; g.fillRect(0, y0, W, h);
    const tunnel = (this.route.track || []).find((t) => this.d >= t[0] && this.d <= t[1])?.[2] !== 'elevated';
    const atStation = this.atStop();
    const door = at(COACH.door);
    for (const r of [...COACH.windows.map(at), door]) {
      g.save(); g.beginPath(); g.rect(r.x, r.y, r.w, r.h); g.clip();
      this.drawOutside(g, r, px, atStation, tunnel, r === door);
      g.restore();
    }
    // the leaves, each sliding its own width: shut they meet at `split`, open they are wholly outside the doorway
    const open = this.doorOpen(), slid = open * open * (3 - 2 * open);
    if (this.leaves.complete && this.leaves.naturalWidth > 0) {
      const L = this.leaves, sw = L.naturalWidth * COACH.door.split, lw = door.w * COACH.door.split, rw = door.w - lw;
      g.save(); g.beginPath(); g.rect(door.x, door.y, door.w, door.h); g.clip();
      g.drawImage(L, 0, 0, sw, L.naturalHeight, door.x - slid * lw, door.y, lw, door.h);
      g.drawImage(L, sw, 0, L.naturalWidth - sw, L.naturalHeight, door.x + lw + slid * rw, door.y, rw, door.h);
      g.restore();
    }
    if (this.coach.complete && this.coach.naturalWidth > 0) g.drawImage(this.coach, px, py, pw, ph);
    this.drawPanel(g, at(COACH.panel));
    const ahead = this.nextStation(); if (ahead) this.stationArt(ahead.name); // fetched before the doors open on it
    // passengers: you, your friends, and the commuters, in their east and west idle frames, at the plate's own scale
    // (a 1.75 m body is `person` of its height), on its floor, in slots a whole number of metres from the doorway
    const metre = (ph * COACH.person) / (1.75 * SCALE), m = metre * SCALE, feet = py + COACH.floor * ph;
    if (!this.people || Math.abs(this.peopleH - ph) > 1) { this.people = new PeopleLayer(metre); this.peopleH = ph; }
    const mid = door.x + door.w * COACH.door.split, left = [...SLOT_M], right = [...SLOT_M];
    const X = (side: number[], sign: number) => mid + sign * (side.shift() ?? SLOT_M[SLOT_M.length - 1] + 0.75) * m;
    const ents: DrawEnt[] = [];
    const n = this.shared ? this.boarded : this.host.companions();
    ents.push({ id: 900, type: 0, x: X(left, -1), y: feet, face: 0 });
    // online, the rest of the kafila aboard stand by the door with you
    if (this.shared) this.shared.aboard().filter(([no]) => no !== this.shared!.me()).forEach(([no], i) => ents.push({ id: 900 + no * 7, type: 0, x: i % 2 ? X(left, -1) : X(right, 1), y: feet + (i % 3) * 0.03 * m, face: i % 2 ? 0 : Math.PI }));
    for (let k = 0; k < n; k++) ents.push({ id: 901 + k, type: 4, k, x: k % 2 ? X(left, -1) : X(right, 1), y: feet + (k % 3) * 0.03 * m, face: k % 2 ? 0 : Math.PI });
    if (this.standing) right.shift(); // they keep a slot's distance from you
    for (let k = 0; k < this.standing; k++) ents.push({ id: 950 + k, type: 4, k: 2 + k, x: X(right, 1), y: feet + 0.08 * m, face: Math.PI });
    for (let i = 0; i < 6; i++) ents.push({ id: 960 + i, type: 2, x: i < 3 ? X(left, -1) : X(right, 1), y: feet - 0.05 * m, face: i < 3 ? 0 : Math.PI });
    ents.sort((a, b) => a.y - b.y);
    g.imageSmoothingEnabled = false; // at this scale an atlas pixel is several screen pixels: keep them hard, not smeared
    for (const b of this.people.standing(ents, 900, 1 / 60, 1, () => metre * 0.6)) b.draw(g);
    g.imageSmoothingEnabled = true;
    const strip = this.drawLed(g, door, at(COACH.panel), y0);
    this.drawScreen(g, W, y0, strip, at(COACH.panel));
    g.restore();
  }

  /**
   * The LED strip over the doorway (section 3.3), as wide as the door: Hindi in amber over English in green, each line
   * rasterised once when its text changes and blitted a whole LED column at a time as it scrolls. It sits above the
   * plate's panel when the window is tall enough, else just under it, over the top of the doorway; either way the
   * panel's route dots stay in view.
   */
  private drawLed(g: CanvasRenderingContext2D, door: Box, panel: Box, y0: number): Box {
    const P = LED.pitch, rows = LED.rows, h = (rows * 2 + 3) * P, w = Math.round(door.w), cols = Math.floor((w - 2 * P) / P);
    const x = Math.round(door.x), y = Math.round(panel.y - 2 - h >= y0 + 4 ? panel.y - 2 - h : panel.y + panel.h + 2);
    const L = ledLines(this.ledPhase(), this.disrupt);
    const font = typeof document === 'undefined' || !document.fonts || document.fonts.check(`600 ${LED.fontPx}px Mukta`);
    if (!this.ledImg || this.ledImg.key !== L.key || this.ledImg.font !== font) // never per frame: only when the words (or the font) change
      this.ledImg = { key: L.key, font, hi: dots(rasterise(L.hi), LED.amber), en: dots(rasterise(L.en), LED.green) };
    if (!this.ledGrid || this.ledGrid.cols !== cols) this.ledGrid = { cols, c: grid(cols, rows * 2 + 1) };
    g.fillStyle = '#0A0B0F'; g.strokeStyle = '#2A2E38'; g.lineWidth = 2;
    g.beginPath(); g.roundRect(x, y, w, h, 4); g.fill(); g.stroke();
    g.drawImage(this.ledGrid.c, x + P, y + P);
    const iw = cols * P;
    [this.ledImg.hi, this.ledImg.en].forEach((img, i) => {
      const ry = y + P + i * (rows + 1) * P, off = Math.round(scrollX(this.ledT, img.width, iw) / P) * P;
      g.save(); g.beginPath(); g.rect(x + P, ry, iw, rows * P); g.clip();
      g.drawImage(img, x + P + off, ry);
      g.restore();
    });
    return { x, y, w, h };
  }

  /**
   * The screen (section 3.4), top-left of the coach: the station ahead (or the one whose doors are open) over its own
   * artwork, both scripts, and the stops after it to the ride's end as dots. In a disruption the art drops out and the
   * line sits alone on black. On a narrow window it moves down, clear of the strip and the panel.
   */
  private drawScreen(g: CanvasRenderingContext2D, W: number, y0: number, strip: Box, panel: Box) {
    const st = this.route.stations || []; if (!st.length) return;
    const sc = screenOf(st, this.ledPhase(), this.endM, this.disrupt);
    const w = Math.round(Math.max(150, Math.min(240, W * 0.17))), h = Math.round(w * 9 / 16), x = 16;
    let y = y0 + 16;
    if (x + w > strip.x && x < strip.x + strip.w) y = Math.max(y, strip.y + strip.h, panel.y + panel.h) + 10;
    const b = 5, ix = x + b, iy = y + b, iw = w - 2 * b, ih = h - 2 * b;
    g.save();
    g.fillStyle = '#0A0B0F'; g.strokeStyle = '#2A2E38'; g.lineWidth = 2;
    g.beginPath(); g.roundRect(x, y, w, h, 6); g.fill(); g.stroke();
    g.beginPath(); g.rect(ix, iy, iw, ih); g.clip();
    g.globalAlpha = this.screenA;
    g.fillStyle = '#000'; g.fillRect(ix, iy, iw, ih);
    g.textAlign = 'left';
    if (sc.disrupt) { // the line alone, on black
      g.fillStyle = PAPER; g.font = `600 ${Math.round(w / 15)}px Inter,system-ui,sans-serif`;
      let ty = iy + w / 12;
      for (const ln of wrap(g, sc.disrupt.en, iw - 16)) { g.fillText(ln, ix + 8, ty); ty += w / 13; }
      g.fillStyle = DIM; g.font = `500 ${Math.round(w / 17)}px Inter,system-ui,sans-serif`; ty += 2;
      for (const ln of wrap(g, sc.disrupt.hi, iw - 16)) { g.fillText(ln, ix + 8, ty); ty += w / 14; }
    } else if (sc.station) {
      const art = this.stationArt(sc.station.name);
      if (art) { // cover the screen, cropped a little below the middle where the building stands against the sky
        const k = Math.max(iw / art.naturalWidth, ih / art.naturalHeight), aw = art.naturalWidth * k, ah = art.naturalHeight * k;
        g.drawImage(art, ix + (iw - aw) / 2, iy + (ih - ah) * 0.6, aw, ah);
      } else { g.fillStyle = '#1C2130'; g.fillRect(ix, iy, iw, ih); }
      const fade = g.createLinearGradient(0, iy + ih * 0.35, 0, iy + ih);
      fade.addColorStop(0, 'rgba(0,0,0,0)'); fade.addColorStop(1, 'rgba(0,0,0,.85)');
      g.fillStyle = fade; g.fillRect(ix, iy, iw, ih);
      const en = Math.round(w / 12), hi = Math.round(w / 16), dotY = iy + ih - 8;
      g.fillStyle = DIM; g.font = `500 ${hi}px Inter,system-ui,sans-serif`; g.fillText(sc.station.hi, ix + 8, dotY - 8 - en, iw - 16);
      g.fillStyle = PAPER; g.font = `600 ${en}px Inter,system-ui,sans-serif`; g.fillText(sc.station.name, ix + 8, dotY - 8, iw - 16);
      // the stops to go: this one lit, the next three after it, and a count of any past those
      const gap = 12, n = sc.ahead.length;
      g.strokeStyle = 'rgba(241,236,224,.35)'; g.lineWidth = 1.5;
      if (n) { g.beginPath(); g.moveTo(ix + 12, dotY); g.lineTo(ix + 12 + n * gap, dotY); g.stroke(); }
      g.fillStyle = SODIUM; g.beginPath(); g.arc(ix + 12, dotY, 3.5, 0, Math.PI * 2); g.fill();
      g.fillStyle = PAPER; sc.ahead.forEach((_, i) => { g.beginPath(); g.arc(ix + 12 + (i + 1) * gap, dotY, 2.2, 0, Math.PI * 2); g.fill(); });
      if (sc.more) { g.font = `500 ${Math.round(w / 20)}px Inter,system-ui,sans-serif`; g.fillText(`+${sc.more}`, ix + 18 + n * gap, dotY + 4); }
    }
    g.restore();
  }

  /** How far the doors stand open, 0 to 1: they open on a station's dwell and on arriving, each way in DOOR_S. */
  private doorOpen() {
    if (this.shared) return this.doorsShared;
    if (this.dwell > 0) return Math.min(1, (STATION_DWELL_S - this.dwell) / DOOR_S, this.dwell / DOOR_S);
    if (this.ending > 0 || this.finished) return Math.min(1, (END_S - Math.max(0, this.ending)) / DOOR_S);
    return 0;
  }

  private atStop() { return this.shared ? this.doorsShared > 0 || trainPos(this.shared.line, this.shared.s()).stop >= 0 : this.dwell > 0 || this.ending > 0 || this.d < 30; }

  /** The station the train stands at: the one being dwelt at, the ride's end, or where it set off. */
  private stationHere() {
    const st = this.route.stations || [];
    if (this.shared) return st[Math.max(0, this.stationAt)];
    if (this.dwell > 0 && this.stationAt >= 0) return st[this.stationAt];
    if (this.ending > 0 || this.finished) return st.find((s) => Math.abs(s.atM - this.endM) < 1) ?? st[st.length - 1];
    return st[0];
  }

  /**
   * A station's artwork once it has loaded, else null (and the doorway shows the plain platform). Asked for the station
   * ahead every frame, so it is fetched on approach; a station with no art in stations.json simply never has any.
   */
  private stationArt(name: string): HTMLImageElement | null {
    let img = this.art.get(name);
    if (!img) {
      const a = (STATIONS.stations as Record<string, { image: string }>)[name];
      if (!a) return null;
      img = new Image(); img.src = a.image; this.art.set(name, img);
    }
    return img.complete && img.naturalWidth > 0 ? img : null;
  }

  /** What is behind the glass: a lit platform at a station (with its edge, through the doorway), tunnel lights, or the city. */
  private drawOutside(g: CanvasRenderingContext2D, r: Box, px: number, atStation: boolean, tunnel: boolean, doorway: boolean) {
    const grid = (gap: number, off: number) => r.x - (((r.x - px + off) % gap) + gap) % gap; // one pattern across every pane
    if (atStation) { // the platform stands still while the train does: its pillars move only with d
      const s = this.stationHere(), art = doorway && s ? this.stationArt(s.name) : null;
      if (art) { // the place itself through the doorway (section 9), drawn to cover it, with the platform's edge before it
        const k = Math.max(r.w / art.naturalWidth, r.h / art.naturalHeight), w = art.naturalWidth * k, h = art.naturalHeight * k;
        g.drawImage(art, r.x + (r.w - w) / 2, r.y + (r.h - h) / 2, w, h);
        g.fillStyle = '#5B606C'; g.fillRect(r.x, r.y + r.h * 0.86, r.w, r.h * 0.14);
        return;
      }
      g.fillStyle = '#9AA0AD'; g.fillRect(r.x, r.y, r.w, r.h);
      g.fillStyle = '#6E7484'; for (let x = grid(90, this.d * 9); x < r.x + r.w; x += 90) g.fillRect(x, r.y, 18, r.h);
      if (doorway) { g.fillStyle = '#5B606C'; g.fillRect(r.x, r.y + r.h * 0.86, r.w, r.h * 0.14); }
      if (s) {
        const bw = Math.min(180, r.w * 0.86), by = r.y + r.h * (doorway ? 0.2 : 0.25);
        g.fillStyle = METRO; g.fillRect(r.x + r.w / 2 - bw / 2, by, bw, 30);
        g.fillStyle = PAPER; g.font = '600 15px Inter,system-ui,sans-serif'; g.textAlign = 'center'; g.fillText(s.name, r.x + r.w / 2, by + 21, bw - 12);
      }
    } else if (tunnel) { // tunnel lights streak past
      g.fillStyle = '#05060A'; g.fillRect(r.x, r.y, r.w, r.h);
      g.strokeStyle = 'rgba(255,214,150,.55)'; g.lineWidth = 3;
      const y = r.y + r.h * (doorway ? 0.3 : 0.5);
      for (let x = grid(160, this.d * 9); x < r.x + r.w + 160; x += 160) { g.beginPath(); g.moveTo(x, y); g.lineTo(x + 70, y); g.stroke(); }
    } else this.drawSkyline(g, r.x, r.y, r.w, r.h, this.d * 3);
  }

  /**
   * The route strip in the lit panel over the doorway (section 6): a dot per station along a line, the one you are
   * approaching (or standing at) lit. The names are on the LED strip (drawLed): the panel is a few pixels tall, too
   * small for words, and nothing in it is baked, so it stays right when the route does.
   */
  private drawPanel(g: CanvasRenderingContext2D, r: Box) {
    const st = this.route.stations || [];
    if (!st.length) return;
    g.fillStyle = '#05060A'; g.fillRect(r.x, r.y, r.w, r.h);
    const here = (this.shared ? this.atStop() : this.dwell > 0 || this.ending > 0 || this.finished) ? this.stationHere() : null;
    const nx = here ?? this.nextStation();
    const cy = r.y + r.h / 2, pad = r.h * 0.6;
    const x0 = r.x + pad, x1 = r.x + r.w - pad, len = this.route.lengthM;
    const X = (at: number) => x0 + (at / len) * (x1 - x0), dot = Math.max(1.5, r.h * 0.14);
    g.strokeStyle = 'rgba(255,122,69,.35)'; g.lineWidth = Math.max(1, r.h * 0.06);
    g.beginPath(); g.moveTo(x0, cy); g.lineTo(x1, cy); g.stroke();
    const blink = here || Math.floor(this.t * 2.5) % 2 === 0;
    for (const s of st) {
      const lit = s === nx;
      g.fillStyle = lit ? (blink ? '#FFB547' : '#7A3A22') : s.atM <= this.d ? 'rgba(255,122,69,.45)' : '#4A4F5E';
      g.beginPath(); g.arc(X(s.atM), cy, lit ? dot * 1.5 : dot, 0, Math.PI * 2); g.fill();
    }
  }

  private drawSkyline(g: CanvasRenderingContext2D, x0: number, y0: number, w: number, h: number, scroll: number) {
    const grad = g.createLinearGradient(0, y0, 0, y0 + h);
    grad.addColorStop(0, '#1B1A2E'); grad.addColorStop(0.6, '#4A2F45'); grad.addColorStop(1, '#B5603F');
    g.fillStyle = grad; g.fillRect(x0, y0, w, h);
    this.sky.forEach((row, layer) => {
      const k = layer ? 0.5 : 0.2, base = y0 + h, span = 2400;
      g.fillStyle = layer ? '#1A1C29' : '#242539';
      for (const b of row) {
        let x = x0 + ((b.x - scroll * k) % span + span) % span - 200;
        for (; x < x0 + w + 200; x += span) {
          const bh = b.h * (h / 600);
          g.fillRect(x, base - bh, b.w, bh);
          g.fillStyle = 'rgba(255,196,110,.55)';
          b.lit.forEach((v, i) => { if (v > 0.55) g.fillRect(x + 8 + (i % 4) * (b.w / 4.5), base - bh + 10 + Math.floor(i / 4) * 22, 6, 9); });
          g.fillStyle = layer ? '#1A1C29' : '#242539';
        }
      }
    });
  }

  /** The cab from the side: a dusk skyline, sodium lamps, the road, the cab in the near lane and the traffic. */
  private drawStreet(g: CanvasRenderingContext2D, W: number, y0: number, h: number) {
    const roadY = y0 + h * 0.68;
    this.drawSkyline(g, 0, y0, W, roadY - y0, this.d * 6);
    // street lights, parallax 1
    const ppm = W / 60, gap = 40 * ppm, off = (this.d * 6 * 1.6) % gap;
    for (let x = -off; x < W + gap; x += gap) {
      g.fillStyle = '#4A4F5E'; g.fillRect(x, roadY - h * 0.38, 4, h * 0.38); g.fillRect(x, roadY - h * 0.38, 40, 4);
      const lamp = g.createRadialGradient(x + 40, roadY - h * 0.37, 2, x + 40, roadY - h * 0.37, 50);
      lamp.addColorStop(0, 'rgba(255,181,71,.65)'); lamp.addColorStop(1, 'rgba(255,181,71,0)');
      g.fillStyle = lamp; g.fillRect(x - 10, roadY - h * 0.37 - 50, 100, 100);
    }
    g.fillStyle = '#23252F'; g.fillRect(0, roadY, W, y0 + h - roadY);
    g.fillStyle = '#8A8478'; g.fillRect(0, roadY, W, 3);
    g.fillStyle = 'rgba(241,236,224,.6)';
    const dash = 6 * ppm, doff = (this.d * 6 * 1.6) % (dash * 2);
    for (let x = -doff; x < W; x += dash * 2) g.fillRect(x, roadY + (y0 + h - roadY) * 0.45, dash, 4);
    // the road's name
    const road = (this.route.roads || []).find((r) => this.d >= r[0] && this.d <= r[1]);
    if (road) {
      g.font = '600 15px Inter,system-ui,sans-serif'; const tw = g.measureText(road[2]).width + 24;
      g.fillStyle = 'rgba(42,36,56,.9)'; g.fillRect(16, y0 + 14, tw, 28); g.fillStyle = PAPER; g.textAlign = 'left'; g.fillText(road[2], 28, y0 + 33);
    }
    const loaded = this.img.complete && this.img.naturalWidth > 0;
    const far = roadY + (y0 + h - roadY) * 0.32, near = roadY + (y0 + h - roadY) * 0.8, cabX = W * 0.4;
    const moving = !this.card && this.jam <= 0 && this.dwell <= 0 && this.ending <= 0;
    const vehicle = (kind: string, cx: number, base: number, flip: boolean, spin: boolean) => {
      const s = SPR[kind], scale = (s.lengthM * ppm) / s.w, w = s.w * scale, hh = s.h * scale;
      if (!loaded) { g.fillStyle = kind === 'cab' ? PAPER : '#6B7080'; g.fillRect(cx - w / 2, base - hh, w, hh); return; }
      const f = s.frames[spin ? Math.floor(this.t * s.fps) % s.frames.length : 0];
      g.save(); g.translate(cx, base - hh);
      if (flip) g.scale(-1, 1);
      g.drawImage(this.img, f[0], f[1], s.w, s.h, -w / 2, 0, w, hh);
      g.restore();
    };
    for (const p of this.passers.filter((q) => q.lane === 1)) vehicle(p.kind, cabX + p.x * ppm, far, true, true);
    for (const p of this.passers.filter((q) => q.lane === 0)) vehicle(p.kind, cabX + p.x * ppm, near, false, moving || p.v > 0.5);
    vehicle('cab', cabX, near, false, moving);
  }

  private drawCard(g: CanvasRenderingContext2D, W: number, y: number) {
    const e = this.card!.event, w = Math.min(680, W - 32), x = (W - w) / 2, v = this.shared?.vote() ?? null;
    const h = (e.say.hi ? 150 : 128) + (v ? 24 : 0);
    g.fillStyle = 'rgba(18,22,32,.96)'; g.strokeStyle = SODIUM; g.lineWidth = 2;
    g.beginPath(); g.roundRect(x, y, w, h, 10); g.fill(); g.stroke();
    let ty = y + 30;
    if (e.kind === 'rumour') { // always labelled as a forward, never in the game's own voice (red lines)
      g.font = '600 11px "IBM Plex Mono",ui-monospace,monospace'; g.fillStyle = NIGHT;
      g.fillStyle = '#F2C230'; g.fillRect(x + 20, y + 12, 78, 18); g.fillStyle = NIGHT; g.textAlign = 'left'; g.fillText('FORWARDED', x + 25, y + 25);
      ty += 12;
    }
    g.textAlign = 'left'; g.fillStyle = PAPER; g.font = '600 17px Inter,system-ui,sans-serif';
    g.fillText(e.say.en, x + 20, ty, w - 40);
    if (e.say.hi) { g.fillStyle = DIM; g.font = '400 14px Inter,system-ui,sans-serif'; g.fillText(e.say.hi, x + 20, ty + 20, w - 40); }
    if (v) { // a vote of everyone aboard: what it is for, how long is left, and that the majority decides
      const left = Math.max(0, Math.ceil(v.left)), aboard = this.shared!.aboard().length;
      g.fillStyle = SODIUM; g.font = '600 13px Inter,system-ui,sans-serif';
      g.fillText(`${v.mine ? 'You voted' : 'Vote, 1 or 2'} · ${left} s left · the majority of the ${aboard} aboard decides for the coach`, x + 20, y + h - 70, w - 40);
    }
    const by = y + h - 56, bw = (w - 56) / 2;
    this.hits = [];
    e.choices.forEach((c, i) => {
      const bx = x + 20 + i * (bw + 16), mine = v?.mine === c.id;
      g.fillStyle = mine ? '#4A3A2A' : DUSK; g.strokeStyle = mine ? SODIUM : 'rgba(241,236,224,.3)'; g.lineWidth = mine ? 2.5 : 1.5;
      g.beginPath(); g.roundRect(bx, by, bw, 44, 6); g.fill(); g.stroke();
      if (v) { g.textAlign = 'right'; g.fillStyle = SODIUM; g.font = '400 20px "Bebas Neue",Impact,sans-serif'; g.fillText(String(v.n[c.id] ?? 0), bx + bw - 12, by + 20); g.textAlign = 'left'; }
      g.fillStyle = PAPER; g.font = '600 14px Inter,system-ui,sans-serif'; g.fillText(`${i + 1}  ${c.label.en}`, bx + 12, by + 19, bw - 44);
      g.fillStyle = DIM; g.font = '400 12px Inter,system-ui,sans-serif'; g.fillText(this.subline(e, c), bx + 28, by + 36, bw - 40);
      this.hits.push({ x: bx, y: by, w: bw, h: 44, id: c.id });
    });
  }

  /** What a choice costs or brings, in a few words, from its effect. */
  private subline(e: JourneyEvent, c: JourneyChoice): string {
    const f = c.effect;
    if (typeof f.lateMin === 'number') return `+${f.lateMin} min`;
    if (typeof f.lateMin === 'string') {
      const walk = this.R[f.lateMin.split(' ')[0]];
      return walk ? `walk ${walk.lengthM} m, about ${Math.round(walk.lengthM / this.J.model.walkMPerMin)} min` : '';
    }
    if (f.companions) return `+${f.companions} with you`;
    if (f.skip === 'gather') return 'on the road early; the gate gathers without you';
    if (e.kind === 'rumour') return 'the display still says Patel Chowk';
    if (f.arrive) return 'where the others meet';
    return '';
  }

  /** Debug hook: where the ride is. */
  state() { return { shared: !!this.shared, vote: this.shared?.vote()?.ev ?? null, ride: this.ride, d: Math.round(this.d), endM: this.endM, card: this.card?.event.id ?? null, clock: clock(this.clockMin()), finished: this.finished, art: this.atStop() ? (this.stationArt(this.stationHere()?.name ?? '') ? this.stationHere()!.name : null) : null, doors: Math.round(this.doorOpen() * 100) / 100, led: this.route.mode === 'metro' ? ledLines(this.ledPhase(), this.disrupt) : null, ledT: Math.round(this.ledT * 10) / 10, disrupt: this.disrupt }; }
}
